import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Check, RefreshCw, X } from "lucide-react";
import { AppShell, type AppShellContext } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type Role = "owner" | "member";

type Member = {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  role: Role;
  joinedAt: string;
  isCurrentUser: boolean;
};

type PendingInvite = {
  id: string;
  email: string;
  role: Role;
  sentAt: string;
};

type TeamResp = {
  organization: { name: string };
  members: Member[];
  pendingInvites: PendingInvite[];
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function RolePill({ role }: { role: Role }) {
  if (role === "owner") {
    return (
      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900">
        Owner
      </span>
    );
  }
  return (
    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
      Member
    </span>
  );
}

export default function DashboardTeam() {
  return <AppShell>{(ctx) => <Inner ctx={ctx} />}</AppShell>;
}

function Inner({ ctx }: { ctx: AppShellContext }) {
  if (!ctx.isOwner) {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Team is owner-only
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Contact your account owner to manage team members.
        </p>
        <Link
          to="/dashboard"
          className="mt-6 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          ← Back to dashboard
        </Link>
      </div>
    );
  }
  return <OwnerView ctx={ctx} />;
}

function OwnerView({ ctx }: { ctx: AppShellContext }) {
  const [data, setData] = useState<TeamResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [organizationNameDraft, setOrganizationNameDraft] = useState("");
  const [savingOrganization, setSavingOrganization] = useState(false);
  const [organizationNotice, setOrganizationNotice] = useState<string | null>(
    null
  );
  const [organizationError, setOrganizationError] = useState<string | null>(
    null
  );
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteSentTo, setInviteSentTo] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    id: string;
    type: "resend" | "rescind" | "transfer";
  } | null>(null);
  const [teamActionNotice, setTeamActionNotice] = useState<string | null>(null);
  const [teamActionError, setTeamActionError] = useState<string | null>(null);

  async function load() {
    const res = await api<TeamResp>("/customer/team");
    if (!res.ok) {
      setError(res.error ?? "Failed to load team.");
      return;
    }
    setError(null);
    const nextData =
      res.data ?? {
        organization: { name: "" },
        members: [],
        pendingInvites: [],
      };
    setData(nextData);
    setOrganizationNameDraft(nextData.organization.name);
  }

  useEffect(() => {
    load();
  }, []);

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteError(null);
    setInviteSentTo(null);
    setTeamActionError(null);
    setTeamActionNotice(null);
    const res = await api<{ ok: boolean; sentTo: string }>(
      "/customer/team/invite",
      {
        method: "POST",
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: "member",
        }),
      }
    );
    setInviting(false);
    if (!res.ok) {
      setInviteError(res.error ?? "Could not send invite.");
      return;
    }
    setInviteSentTo(res.data?.sentTo ?? inviteEmail.trim());
    setInviteEmail("");
    await load();
  }

  async function onSaveOrganization(e: React.FormEvent) {
    e.preventDefault();
    const name = organizationNameDraft.trim();
    if (!name || name === data?.organization.name) return;

    setSavingOrganization(true);
    setOrganizationError(null);
    setOrganizationNotice(null);
    setTeamActionError(null);
    setTeamActionNotice(null);

    const res = await api<{
      ok: true;
      organization: { name: string };
    }>("/customer/team/organization", {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });
    setSavingOrganization(false);

    if (!res.ok || !res.data) {
      setOrganizationError(res.error ?? "Could not update organization name.");
      return;
    }

    const nextName = res.data.organization.name;
    setData((current) =>
      current
        ? { ...current, organization: { name: nextName } }
        : current
    );
    setOrganizationNameDraft(nextName);
    setOrganizationNotice("Organization name updated.");
    ctx.onOrgRenamed(nextName);
  }

  async function onResendInvite(invite: PendingInvite) {
    setPendingAction({ id: invite.id, type: "resend" });
    setTeamActionError(null);
    setTeamActionNotice(null);
    const res = await api(`/customer/team/invites/${invite.id}/resend`, {
      method: "POST",
    });
    setPendingAction(null);
    if (!res.ok) {
      setTeamActionError(res.error ?? "Could not resend invite.");
      return;
    }
    setTeamActionNotice(`Invite resent to ${invite.email}.`);
    await load();
  }

  async function onRescindInvite(invite: PendingInvite) {
    if (!window.confirm(`Rescind the pending invite for ${invite.email}?`)) {
      return;
    }
    setPendingAction({ id: invite.id, type: "rescind" });
    setTeamActionError(null);
    setTeamActionNotice(null);
    const res = await api(`/customer/team/invites/${invite.id}`, {
      method: "DELETE",
    });
    setPendingAction(null);
    if (!res.ok) {
      setTeamActionError(res.error ?? "Could not rescind invite.");
      return;
    }
    setTeamActionNotice(`Invite rescinded for ${invite.email}.`);
    await load();
  }

  async function onRemove(member: Member) {
    if (member.isCurrentUser) return;
    if (member.role === "owner") {
      const owners = data?.members.filter((m) => m.role === "owner") ?? [];
      if (owners.length <= 1) {
        alert("Can't remove the last owner.");
        return;
      }
    }
    if (!window.confirm(`Remove ${member.email} from this account?`)) return;
    const res = await api(`/customer/team/members/${member.id}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      alert(res.error ?? "Could not remove member.");
      return;
    }
    await load();
  }

  async function onTransferOwner(member: Member) {
    if (member.isCurrentUser) return;
    if (
      !window.confirm(
        `Transfer account ownership to ${member.email}? Your role will change to member.`
      )
    ) {
      return;
    }
    setPendingAction({ id: member.id, type: "transfer" });
    setTeamActionError(null);
    setTeamActionNotice(null);
    const res = await api<{ ok: true; ownerEmail: string }>(
      `/customer/team/members/${member.id}/transfer-owner`,
      { method: "POST" }
    );
    setPendingAction(null);
    if (!res.ok) {
      setTeamActionError(res.error ?? "Could not transfer ownership.");
      return;
    }
    setTeamActionNotice(
      `Ownership transferred to ${res.data?.ownerEmail ?? member.email}.`
    );
    await load();
    window.location.reload();
  }

  const ownerCount =
    data?.members.filter((m) => m.role === "owner").length ?? 0;

  return (
    <div className="mx-auto w-full max-w-4xl px-6 py-8">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Team
          </h1>
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
            Members of your Reputely account.
          </p>
        </div>
      </div>

      {error && (
        <p className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </p>
      )}

      {!data && !error && (
        <div className="mt-8 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {data && (
        <>
          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Organization
            </div>
            <form
              onSubmit={onSaveOrganization}
              className="mt-4 flex flex-wrap items-end gap-3"
            >
              <div className="min-w-[240px] flex-1">
                <label
                  htmlFor="organization-name"
                  className="block text-xs font-medium text-slate-600 dark:text-slate-400"
                >
                  Name
                </label>
                <input
                  id="organization-name"
                  type="text"
                  required
                  maxLength={80}
                  value={organizationNameDraft}
                  onChange={(e) => {
                    setOrganizationNameDraft(e.target.value);
                    setOrganizationError(null);
                    setOrganizationNotice(null);
                  }}
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-slate-500 dark:focus:ring-slate-500"
                />
              </div>
              <Button
                type="submit"
                disabled={
                  savingOrganization ||
                  !organizationNameDraft.trim() ||
                  organizationNameDraft.trim() === data.organization.name
                }
              >
                {savingOrganization ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save
              </Button>
            </form>
            {organizationError && (
              <p className="mt-3 text-sm text-destructive" role="alert">
                {organizationError}
              </p>
            )}
            {organizationNotice && !organizationError && (
              <p className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                <Check className="h-4 w-4" /> {organizationNotice}
              </p>
            )}
          </section>

          {(teamActionError || teamActionNotice) && (
            <div
              className={`mt-6 rounded-xl border p-4 text-sm ${
                teamActionError
                  ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
              }`}
              role={teamActionError ? "alert" : "status"}
            >
              {teamActionError ?? teamActionNotice}
            </div>
          )}

          <section className="mt-6 rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            <div className="border-b border-slate-200 px-6 py-4 text-sm font-medium text-slate-900 dark:border-slate-800 dark:text-slate-100">
              Members
            </div>
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {data.members.map((m) => {
                const isLastOwner = m.role === "owner" && ownerCount <= 1;
                const removeDisabled = m.isCurrentUser || isLastOwner;
                const isTransferring =
                  pendingAction?.id === m.id && pendingAction.type === "transfer";
                return (
                  <li
                    key={m.id}
                    className="flex items-center justify-between gap-4 px-6 py-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          {m.name || m.email}
                        </span>
                        {m.isCurrentUser && (
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            (you)
                          </span>
                        )}
                      </div>
                      {m.name && (
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {m.email}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Joined {formatDate(m.joinedAt)}
                    </div>
                    <RolePill role={m.role} />
                    <div className="flex items-center gap-2">
                      {!m.isCurrentUser && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onTransferOwner(m)}
                          disabled={pendingAction !== null}
                        >
                          {isTransferring && (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          )}
                          Make owner
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRemove(m)}
                        disabled={removeDisabled}
                        className="text-destructive hover:text-destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          {data.pendingInvites.length > 0 && (
            <section className="mt-6 rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              <div className="border-b border-slate-200 px-6 py-4 text-sm font-medium text-slate-900 dark:border-slate-800 dark:text-slate-100">
                Pending invites
              </div>
              <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                {data.pendingInvites.map((p) => {
                  const isResending =
                    pendingAction?.id === p.id &&
                    pendingAction.type === "resend";
                  const isRescinding =
                    pendingAction?.id === p.id &&
                    pendingAction.type === "rescind";
                  const actionInProgress = pendingAction !== null;
                  return (
                    <li
                      key={p.id}
                      className="flex flex-wrap items-center justify-between gap-4 px-6 py-4"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-slate-900 dark:text-slate-100">
                          {p.email}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          Sent {formatDate(p.sentAt)}
                        </div>
                      </div>
                      <RolePill role={p.role} />
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={() => onResendInvite(p)}
                          disabled={actionInProgress}
                          title={`Resend invite to ${p.email}`}
                        >
                          {isResending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                          Resend
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={() => onRescindInvite(p)}
                          disabled={actionInProgress}
                          className="text-destructive hover:text-destructive"
                          title={`Rescind invite for ${p.email}`}
                        >
                          {isRescinding ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                          Rescind
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Invite a teammate
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              They'll get an email with a sign-in link.
            </p>
            <form
              onSubmit={onInvite}
              className="mt-4 flex flex-wrap items-end gap-3"
            >
              <div className="min-w-[240px] flex-1">
                <label
                  htmlFor="invite-email"
                  className="block text-xs font-medium text-slate-600 dark:text-slate-400"
                >
                  Email
                </label>
                <input
                  id="invite-email"
                  type="email"
                  required
                  placeholder="teammate@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-slate-500 dark:focus:ring-slate-500"
                />
              </div>
              <Button type="submit" disabled={inviting || !inviteEmail.trim()}>
                {inviting && <Loader2 className="h-4 w-4 animate-spin" />}
                {inviting ? "Sending…" : "Send invite"}
              </Button>
            </form>
            {inviteError && (
              <p className="mt-3 text-sm text-destructive" role="alert">
                {inviteError}
              </p>
            )}
            {inviteSentTo && !inviteError && (
              <p className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                <Check className="h-4 w-4" /> Invite sent to {inviteSentTo}.
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
